import { useState } from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import { match } from 'ts-pattern';

import { useBreakpointDown } from '@nesso/ui/hooks/use-breakpoint-down';
import { cn } from '@nesso/ui/lib/utils';

import { useRecording, useRecordingStore } from '@/entities/audio-recording';
import type { GetClientByIdOutput } from '@/entities/client';
import {
    getMaxSessionDurationSeconds,
    getMinSessionDurationSeconds,
    type InputMethod,
    SessionRecordingProvider,
} from '@/entities/session';
import { ConnectionAlert } from '@/shared/ui/connection-alert/connection-alert';

import { RecordingCapture } from './recording-session/recording-capture';
import { RecordingHeader } from './recording-session/recording-header';
import { RecordingLaunch } from './recording-session/recording-launch';
import { RecordingRecapCard } from './recording-session/recording-recap-card';
import { RecordingRecapChecklistButton } from './recording-session/recording-recap-checklist-button';

type Props = {
    client: GetClientByIdOutput;
    inputMethod: InputMethod;
};

type RecapCardState = 'open' | 'closing' | 'closed';

const RECAP_CARD_SLIDE = { duration: 0.5, ease: 'easeInOut' } as const;

export function RecordingSession({ client, inputMethod }: Props) {
    useRecording(
        client.id,
        getMinSessionDurationSeconds() * 1000,
        getMaxSessionDurationSeconds() * 1000,
    );

    const status = useRecordingStore((state) => state.status);
    const isMobile = useBreakpointDown('md');

    const [recapCardState, setRecapCardState] = useState<RecapCardState>('open');
    const isRecapCardOpen = recapCardState === 'open';
    const hasRecapCardExited = recapCardState === 'closed';
    const offscreen = isMobile ? { y: '100%' } : { x: '100%' };

    return (
        <div
            className={cn(
                'relative h-full overflow-hidden transition-colors duration-300 ease-in-out',
                '[--recap-card-width:--spacing(96)] lg:[--recap-card-width:--spacing(128)] xl:[--recap-card-width:--spacing(160)]',
                status === 'paused' && 'bg-recording-paused',
            )}
        >
            <div
                className={cn(
                    'flex h-full flex-col transition-[padding] duration-500 ease-in-out',
                    isRecapCardOpen &&
                        'max-md:pb-[40vh] md:pr-[calc(var(--recap-card-width)+--spacing(6))]',
                )}
            >
                <RecordingHeader clientName={client.name}>
                    {hasRecapCardExited && !isMobile && (
                        <RecordingRecapChecklistButton onClick={() => setRecapCardState('open')} />
                    )}
                </RecordingHeader>
                <SessionRecordingProvider
                    sessionType="in-person"
                    platform="chrome"
                    inputMethod={inputMethod}
                >
                    <div
                        className={cn(
                            'relative flex flex-col items-center justify-center px-6 sm:flex-1',
                            hasRecapCardExited && 'max-sm:flex-1',
                        )}
                    >
                        <ConnectionAlert />
                        {match(status)
                            .with('idle', () => (
                                <div className="flex flex-col flex-1 items-center justify-center xs:contents">
                                    <RecordingLaunch />
                                </div>
                            ))
                            .otherwise(() => (
                                <RecordingCapture />
                            ))}
                    </div>
                </SessionRecordingProvider>
            </div>
            {isMobile && hasRecapCardExited && (
                <RecordingRecapChecklistButton
                    className="absolute bottom-4 left-1/2 -translate-x-1/2"
                    onClick={() => setRecapCardState('open')}
                />
            )}
            <AnimatePresence
                onExitComplete={() =>
                    setRecapCardState((state) => (state === 'closing' ? 'closed' : state))
                }
            >
                {isRecapCardOpen && (
                    <motion.div
                        key="recap-card"
                        initial={offscreen}
                        animate={{ x: 0, y: 0 }}
                        exit={offscreen}
                        transition={RECAP_CARD_SLIDE}
                        className="absolute inset-x-0 bottom-0 p-3 md:inset-y-0 md:left-auto md:right-0 md:w-[calc(var(--recap-card-width)+--spacing(6))]"
                    >
                        <RecordingRecapCard onClose={() => setRecapCardState('closing')} />
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
